import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PageConsensusBanner from '../components/PageConsensusBanner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown, ArrowRight, TrendUp } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClusterBadge = ({ cluster }) => {
  const colors = ['bg-blue-100 text-blue-800', 'bg-green-100 text-green-800', 'bg-amber-100 text-amber-800', 'bg-purple-100 text-purple-800', 'bg-red-100 text-red-800'];
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${colors[cluster] || colors[0]}`} data-testid={`cluster-${cluster}`}>C{cluster}</span>;
};

const MarkovPredictionSummary = ({ markov }) => {
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const ballLabels = ['B1', 'B2', 'B3', 'B4', 'B5'];

  // Extract #1 prediction per ball
  const topPicks = ballKeys.map((bk, i) => {
    const mk = markov?.[bk] || {};
    const top = (mk.next_probable || [])[0];
    return { label: ballLabels[i], key: bk, last: mk.last_number, number: top?.number, prob: top?.prob, count: top?.count };
  });

  // Build sorted combination
  const comboNums = topPicks.map(p => p.number).filter(Boolean);
  const sortedCombo = [...comboNums].sort((a, b) => a - b);

  return (
    <div className="bg-gradient-to-br from-[#001d66] via-[#002FA7] to-[#0040dd] rounded-xl p-6 text-white shadow-xl mb-6" data-testid="markov-prediction-summary">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
          <TrendUp size={22} weight="bold" className="text-amber-300" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Markov Chain Prediction</h2>
          <p className="text-xs text-white/60">Most probable next number per position based on transition probabilities</p>
        </div>
      </div>

      {/* Top combination */}
      <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-5 border border-white/10" data-testid="markov-top-combo">
        <div className="text-[10px] font-bold text-white/50 uppercase tracking-wider mb-2">Predicted Combination (sorted ascending)</div>
        <div className="flex items-center gap-3 justify-center">
          {sortedCombo.map((num, idx) => (
            <div key={idx} className="relative">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <span className="font-mono font-bold text-2xl text-[#001d66]">{num}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-position breakdown with top 3 candidates */}
      <div className="grid grid-cols-5 gap-3" data-testid="markov-per-ball-summary">
        {topPicks.map((pick, idx) => {
          const mk = markov?.[pick.key] || {};
          const top3 = (mk.next_probable || []).slice(0, 3);
          return (
            <div key={pick.key} className="bg-white/8 rounded-lg p-3 border border-white/10" data-testid={`markov-summary-${pick.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-white/50 uppercase">{pick.label}</span>
                <span className="text-[10px] text-white/40 font-mono">was {pick.last}</span>
              </div>
              <div className="space-y-1.5">
                {top3.map((n, ni) => (
                  <div key={n.number} className={`flex items-center gap-2 rounded px-2 py-1 ${ni === 0 ? 'bg-amber-400/20 border border-amber-400/30' : 'bg-white/5'}`}>
                    <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-sm ${ni === 0 ? 'bg-amber-400 text-[#001d66]' : 'bg-white/15 text-white'}`}>{n.number}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold ${ni === 0 ? 'text-amber-300' : 'text-white/70'}`}>{n.prob}%</span>
                        <span className="text-[10px] text-white/40">{n.count}x</span>
                      </div>
                      {/* Probability bar */}
                      <div className="h-1 bg-white/10 rounded-full mt-0.5">
                        <div className={`h-full rounded-full ${ni === 0 ? 'bg-amber-400' : 'bg-white/30'}`} style={{width: `${Math.min(n.prob * 2, 100)}%`}} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Transition path */}
      <div className="mt-4 flex items-center gap-2 text-[10px] text-white/40 justify-center" data-testid="markov-transition-path">
        <span className="font-bold">Transition path:</span>
        {topPicks.map((pick, idx) => (
          <React.Fragment key={pick.key}>
            <span className="font-mono">{pick.last}</span>
            <ArrowRight size={10} className="text-amber-400/60" />
            <span className="font-mono font-bold text-amber-300">{pick.number || '?'}</span>
            {idx < topPicks.length - 1 && <span className="text-white/20 mx-1">|</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default function Window19MLPatterns() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/ml-patterns`); setData(res.data); }
      catch { toast.error('Failed to fetch Markov analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 19</h1><p className="text-xs text-gray-500">Markov Chain Analysis</p></div>
            </div>
          </div>

            <ExportButton data={data?.anomalies} filename="w19_markov_analysis" />
          </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W19" {...NARRATIVES.W19} className="mb-5" />
        <PageConsensusBanner className="mb-4" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <>
          {/* Prediction Summary Panel — always visible at top */}
          {data.markov && <MarkovPredictionSummary markov={data.markov} />}

          <Tabs defaultValue="markov" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              <TabsTrigger value="markov" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-markov">Markov Chain</TabsTrigger>
              <TabsTrigger value="clusters" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-clusters">Clustering</TabsTrigger>
              <TabsTrigger value="anomalies" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-anomalies">Anomaly Detection</TabsTrigger>
            </TabsList>

            {/* Markov Chain Tab */}
            <TabsContent value="markov">
              <div className="space-y-6">
                {Object.entries(ballLabels).map(([key, label]) => {
                  const mk = data.markov?.[key] || {};
                  return (
                    <div key={key} className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1">{label} — Markov Chain Prediction</h3>
                      <p className="text-xs text-gray-500 mb-3">Last drawn: <span className="font-mono font-bold text-[#002FA7] text-sm">{mk.last_number}</span> — Most probable next numbers:</p>
                      <div className="flex gap-2 flex-wrap mb-4" data-testid={`markov-next-${key}`}>
                        {(mk.next_probable || []).map((n, idx) => (
                          <div key={n.number} className={`px-3 py-2 rounded-lg text-center ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-gray-100'}`}>
                            <div className="font-mono font-bold text-lg">{n.number}</div>
                            <div className="text-[10px] font-bold">{n.prob}% ({n.count}x)</div>
                          </div>
                        ))}
                      </div>
                      <details className="text-xs">
                        <summary className="cursor-pointer font-bold text-gray-500 hover:text-gray-700">Top 20 Transitions Overall</summary>
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1">
                          {(mk.top_transitions || []).map((t, i) => (
                            <div key={i} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded">
                              <span className="font-mono font-bold">{t.from}</span>
                              <span className="text-gray-400">→</span>
                              <span className="font-mono font-bold">{t.to}</span>
                              <span className="text-gray-400 ml-auto">{t.count}x ({t.prob}%)</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Clustering Tab */}
            <TabsContent value="clusters">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Total Draws</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="stat-total">{data.total_draws}</p>
                </div>
                <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200">
                  <p className="text-xs uppercase font-bold text-amber-600">Last Draw Cluster</p>
                  <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stat-last-cluster"><ClusterBadge cluster={data.last_cluster} /></p>
                </div>
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Total Clusters</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">5 (K-Means)</p>
                </div>
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Last Anomaly Score</p>
                  <p className={`text-2xl font-bold mt-1 ${data.last_anomaly_score > 1.5 ? 'text-red-600' : 'text-green-700'}`}>{data.last_anomaly_score}</p>
                </div>
              </div>

              <div className="bg-white border rounded p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Cluster Profiles (K-Means, k=5)</h3>
                <div className="overflow-x-auto" data-testid="clusters-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2">Cluster</th>
                        <th className="px-3 py-2 text-center">Size</th>
                        <th className="px-3 py-2 text-center">%</th>
                        <th className="px-3 py-2 text-center">Avg Sum</th>
                        <th className="px-3 py-2 text-center">Avg Spread</th>
                        <th className="px-3 py-2 text-center">Avg Odds</th>
                        <th className="px-3 py-2 text-center">Avg High</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.clusters.map((c, idx) => (
                        <tr key={c.cluster} className={`${c.cluster === data.last_cluster ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${c.cluster === data.last_cluster ? 'ring-2 ring-[#002FA7]' : ''}`} style={c.cluster === data.last_cluster ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}}>
                          <td className="px-3 py-2 border-b"><ClusterBadge cluster={c.cluster} /></td>
                          <td className="px-3 py-2 border-b font-mono text-center font-bold">{c.size}</td>
                          <td className="px-3 py-2 border-b font-mono text-center">{c.pct}%</td>
                          <td className="px-3 py-2 border-b font-mono text-center">{c.avg_sum}</td>
                          <td className="px-3 py-2 border-b font-mono text-center">{c.avg_spread}</td>
                          <td className="px-3 py-2 border-b font-mono text-center">{c.avg_odds}</td>
                          <td className="px-3 py-2 border-b font-mono text-center">{c.avg_high}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-3">Recent 10 Draws — Cluster Distribution</h3>
                <div className="flex gap-3 flex-wrap" data-testid="recent-clusters">
                  {Object.entries(data.recent_cluster_dist).sort((a, b) => b[1] - a[1]).map(([cl, cnt]) => (
                    <div key={cl} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded">
                      <ClusterBadge cluster={parseInt(cl)} />
                      <span className="font-mono font-bold">{cnt}x</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Anomaly Detection Tab */}
            <TabsContent value="anomalies">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-1">Top 20 Anomalous Draws</h3>
                <p className="text-xs text-gray-500 mb-3">Draws with unusual feature combinations (Z-score based detection)</p>
                <div className="overflow-x-auto" data-testid="anomalies-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center">Draw</th>
                        <th className="px-3 py-2 text-center">Date</th>
                        <th className="px-3 py-2 text-center">Balls</th>
                        <th className="px-3 py-2 text-center">Anomaly Score</th>
                        <th className="px-3 py-2 text-center">Max Z</th>
                        <th className="px-3 py-2 text-center">Severity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.anomalies.map((a, idx) => (
                        <tr key={a.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`anomaly-row-${a.draw}`}>
                          <td className="px-3 py-1.5 border-b font-mono text-center">#{a.draw}</td>
                          <td className="px-3 py-1.5 border-b text-center">{a.date}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{a.balls.join(', ')}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{a.anomaly_score}</td>
                          <td className={`px-3 py-1.5 border-b font-mono text-center font-bold ${a.max_z > 3 ? 'text-red-600' : 'text-orange-600'}`}>{a.max_z}</td>
                          <td className="px-3 py-1.5 border-b text-center">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${a.max_z > 3 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                              {a.max_z > 3 ? 'HIGH' : 'MODERATE'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          </>
        ) : null}
      </main>
    </div>
  );
}
